import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import API_URL from '../config/api';
import PermissionEditor from '../components/PermissionEditor';
import { 
  FiShield, 
  FiUserPlus, 
  FiEdit2, 
  FiTrash2, 
  FiMail, 
  FiPhone,
  FiCheckCircle,
  FiXCircle,
  FiStar,
  FiUsers,
  FiClock,
  FiActivity,
  FiCopy,
  FiEye,
  FiMenu,
  FiMoreVertical,
  FiUserCheck,
  FiUserX,
  FiKey
} from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const Users = () => {
  const { user: currentUser, isOwner } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'active', 'inactive'
  const [stats, setStats] = useState({
    owner: 0,
    admins: 0,
    users: 0,
    maxAdmins: 4,
    availableAdminSlots: 4,
    onlineNow: 0,
    activeToday: 0,
    averageActiveTime: 0,
    pendingApproval: 0
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'user',
    adminId: 1,
    isActive: false,
    permissions: {
      canViewClients: false,
      canCreateClients: false,
      canEditClients: false,
      canDeleteClients: false,
      canProcessPayments: false,
      canViewReports: false,
      canExportData: false,
      canManageUsers: false,
      canViewAuditLogs: false,
      canConfigureSystem: false
    }
  });

  useEffect(() => {
    if (isOwner) {
      fetchUsers();
      fetchStats();
    }
  }, [isOwner]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUsers(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar usuarios');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUserActivity = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}/activity`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setActivityData(response.data);
      setShowActivityModal(true);
      setMobileMenuOpen(null);
    } catch (error) {
      toast.error('Error al cargar actividad del usuario');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.role === 'admin' && !editingUser) {
      if (stats.admins >= 4) {
        toast.error('Ya tienes 4 administradores. No puedes crear más.');
        return;
      }
    }
    
    try {
      if (editingUser) {
        await axios.put(
          `${API_URL}/api/auth/users/${editingUser._id}`,
          formData,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        toast.success('Usuario actualizado');
      } else {
        const response = await axios.post(
          `${API_URL}/api/users`,
          formData,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        
        if (response.data.data.generatedPassword) {
          toast.success(
            <div>
              <p className="font-bold">Usuario creado</p>
              <p className="text-sm">Contraseña: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{response.data.data.generatedPassword}</span></p>
              <button 
                onClick={() => navigator.clipboard.writeText(response.data.data.generatedPassword)}
                className="text-blue-600 text-sm mt-1 flex items-center"
              >
                <FiCopy className="mr-1" /> Copiar contraseña
              </button>
            </div>,
            { duration: 10000 }
          );
        } else {
          toast.success('Usuario creado');
        }
      }
      
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar usuario');
    }
  };

  const handleUpdatePermissions = async () => {
    try {
      await axios.put(
        `${API_URL}/api/users/${selectedUser._id}/permissions`,
        { permissions: selectedUser.permissions },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Permisos actualizados');
      setShowPermissionsModal(false);
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar permisos');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/auth/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Usuario eliminado');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const handleActivateUser = async (userId, activate) => {
    try {
      await axios.put(
        `${API_URL}/api/auth/users/${userId}`,
        { isActive: activate },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(activate ? 'Usuario activado' : 'Usuario desactivado');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error('Error al cambiar estado del usuario');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'user',
      adminId: 1,
      isActive: false,
      permissions: {
        canViewClients: false,
        canCreateClients: false,
        canEditClients: false,
        canDeleteClients: false,
        canProcessPayments: false,
        canViewReports: false,
        canExportData: false,
        canManageUsers: false,
        canViewAuditLogs: false,
        canConfigureSystem: false
      }
    });
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      adminId: user.adminId || 1,
      isActive: user.isActive,
      permissions: user.permissions || formData.permissions
    });
    setShowModal(true);
    setMobileMenuOpen(null);
  };

  const formatActiveTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Filtrar usuarios según el filtro seleccionado
  const filteredUsers = users.filter(user => {
    if (filter === 'all') return true;
    if (filter === 'pending') return user.role === 'user' && !user.isActive;
    if (filter === 'active') return user.isActive;
    if (filter === 'inactive') return !user.isActive;
    if (filter === 'admins') return user.role === 'admin';
    if (filter === 'users') return user.role === 'user';
    return true;
  });

  if (!isOwner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          <FiShield className="mx-auto text-6xl text-red-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800">Acceso Restringido</h2>
          <p className="text-gray-600 mt-2">Solo el owner puede gestionar usuarios</p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 p-2 md:p-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Gestión de Usuarios</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">
            {stats.pendingApproval > 0 && (
              <span className="text-yellow-600 font-semibold">
                {stats.pendingApproval} usuario(s) pendiente(s) de aprobación
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => {
            setEditingUser(null);
            resetForm();
            setShowModal(true);
          }}
          disabled={stats.admins >= 4}
          className="flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
        >
          <FiUserPlus className="mr-2" size={18} />
          Nuevo Usuario
        </button>
      </div>

      {/* Stats Cards - Mejorado */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow p-3 md:p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs opacity-90">Owner</p>
              <p className="text-lg md:text-2xl font-bold">{stats.owner}/1</p>
            </div>
            <FiStar className="text-xl md:text-3xl opacity-75" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow p-3 md:p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs opacity-90">Administradores</p>
              <p className="text-lg md:text-2xl font-bold">{stats.admins}/{stats.maxAdmins}</p>
            </div>
            <FiUsers className="text-xl md:text-3xl opacity-75" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow p-3 md:p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs opacity-90">Usuarios</p>
              <p className="text-lg md:text-2xl font-bold">{stats.users || 0}</p>
            </div>
            <FiUserCheck className="text-xl md:text-3xl opacity-75" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow p-3 md:p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs opacity-90">Pendientes</p>
              <p className="text-lg md:text-2xl font-bold text-white">{stats.pendingApproval || 0}</p>
            </div>
            <FiClock className="text-xl md:text-3xl opacity-75" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow p-3 md:p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs opacity-90">Conectados</p>
              <p className="text-lg md:text-2xl font-bold">{stats.onlineNow}</p>
            </div>
            <FiActivity className="text-xl md:text-3xl opacity-75" />
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-lg text-sm transition-colors ${
              filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-1 rounded-lg text-sm transition-colors ${
              filter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pendientes ({stats.pendingApproval})
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`px-3 py-1 rounded-lg text-sm transition-colors ${
              filter === 'active' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Activos
          </button>
          <button
            onClick={() => setFilter('inactive')}
            className={`px-3 py-1 rounded-lg text-sm transition-colors ${
              filter === 'inactive' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Inactivos
          </button>
          <button
            onClick={() => setFilter('admins')}
            className={`px-3 py-1 rounded-lg text-sm transition-colors ${
              filter === 'admins' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Admins
          </button>
        </div>
      </div>

      {/* Users Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tiempo conectado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Último acceso</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No hay usuarios para mostrar
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          user.role === 'owner' 
                            ? 'bg-purple-100' 
                            : user.role === 'admin'
                              ? 'bg-blue-100'
                              : user.isOnline
                                ? 'bg-green-100'
                                : 'bg-gray-100'
                        }`}>
                          <span className={`font-medium ${
                            user.role === 'owner' 
                              ? 'text-purple-600' 
                              : user.role === 'admin'
                                ? 'text-blue-600'
                                : user.isOnline
                                  ? 'text-green-600'
                                  : 'text-gray-600'
                          }`}>
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          {user.role === 'user' && !user.isActive && (
                            <span className="text-xs text-yellow-600 flex items-center">
                              <span className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></span>
                              Pendiente de aprobación
                            </span>
                          )}
                          {user.isOnline && user.isActive && (
                            <span className="text-xs text-green-600 flex items-center">
                              <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                              En línea
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-600 mb-1">
                        <FiMail className="mr-2 text-gray-400" size={14} />
                        {user.email}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <FiPhone className="mr-2 text-gray-400" size={14} />
                        {user.phone}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        user.role === 'owner' 
                          ? 'bg-purple-100 text-purple-800' 
                          : user.role === 'admin'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role === 'owner' ? 'Dueño' : 
                         user.role === 'admin' ? `Admin ${user.adminId || ''}` : 
                         'Usuario'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        user.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {user.formattedActiveTime || '0h 0m'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {user.loginStats?.lastLogin 
                        ? formatDistanceToNow(new Date(user.loginStats.lastLogin), { addSuffix: true, locale: es })
                        : 'Nunca'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center space-x-2">
                        {/* Botón de activar/desactivar para usuarios pendientes */}
                        {user.role === 'user' && !user.isActive && (
                          <button
                            onClick={() => handleActivateUser(user._id, true)}
                            className="text-green-600 hover:text-green-800"
                            title="Aprobar usuario"
                          >
                            <FiUserCheck size={18} />
                          </button>
                        )}
                        {user.role === 'user' && user.isActive && (
                          <button
                            onClick={() => handleActivateUser(user._id, false)}
                            className="text-yellow-600 hover:text-yellow-800"
                            title="Desactivar usuario"
                          >
                            <FiUserX size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            fetchUserActivity(user._id);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Ver actividad"
                        >
                          <FiEye size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowPermissionsModal(true);
                          }}
                          className="text-purple-600 hover:text-purple-900"
                          title="Configurar permisos"
                        >
                          <FiShield size={18} />
                        </button>
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-green-600 hover:text-green-900"
                          title="Editar"
                        >
                          <FiEdit2 size={18} />
                        </button>
                        {user._id !== currentUser._id && (
                          <button
                            onClick={() => handleDelete(user._id)}
                            className="text-red-600 hover:text-red-900"
                            title="Eliminar"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Users Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">No hay usuarios para mostrar</p>
          </div>
        ) : (
          filteredUsers.map((user) => (
            <div key={user._id} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                    user.role === 'owner' 
                      ? 'bg-purple-100' 
                      : user.role === 'admin'
                        ? 'bg-blue-100'
                        : user.isOnline
                          ? 'bg-green-100'
                          : 'bg-gray-100'
                  }`}>
                    <span className={`text-lg font-medium ${
                      user.role === 'owner' 
                        ? 'text-purple-600' 
                        : user.role === 'admin'
                          ? 'text-blue-600'
                          : user.isOnline
                            ? 'text-green-600'
                            : 'text-gray-600'
                    }`}>
                      {user.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user.name}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        user.role === 'owner' 
                          ? 'bg-purple-100 text-purple-800' 
                          : user.role === 'admin'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role === 'owner' ? 'Dueño' : 
                         user.role === 'admin' ? `Admin ${user.adminId || ''}` : 
                         'Usuario'}
                      </span>
                      {user.role === 'user' && !user.isActive && (
                        <span className="text-xs text-yellow-600 flex items-center">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></span>
                          Pendiente
                        </span>
                      )}
                      {user.isOnline && user.isActive && (
                        <span className="text-xs text-green-600 flex items-center">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                          En línea
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Mobile menu button */}
                <div className="relative">
                  <button
                    onClick={() => setMobileMenuOpen(mobileMenuOpen === user._id ? null : user._id)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <FiMoreVertical size={20} className="text-gray-600" />
                  </button>
                  
                  {mobileMenuOpen === user._id && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setMobileMenuOpen(null)}
                      />
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-50 border">
                        <div className="py-1">
                          {user.role === 'user' && !user.isActive && (
                            <button
                              onClick={() => {
                                handleActivateUser(user._id, true);
                                setMobileMenuOpen(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 flex items-center"
                            >
                              <FiUserCheck className="mr-2 text-green-600" size={16} />
                              Aprobar usuario
                            </button>
                          )}
                          {user.role === 'user' && user.isActive && (
                            <button
                              onClick={() => {
                                handleActivateUser(user._id, false);
                                setMobileMenuOpen(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-yellow-50 flex items-center"
                            >
                              <FiUserX className="mr-2 text-yellow-600" size={16} />
                              Desactivar usuario
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              fetchUserActivity(user._id);
                              setMobileMenuOpen(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center"
                          >
                            <FiEye className="mr-2 text-blue-600" size={16} />
                            Ver actividad
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowPermissionsModal(true);
                              setMobileMenuOpen(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                          >
                            <FiShield className="mr-2 text-purple-600" size={16} />
                            Configurar permisos
                          </button>
                          <button
                            onClick={() => {
                              handleEdit(user);
                              setMobileMenuOpen(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 flex items-center"
                          >
                            <FiEdit2 className="mr-2 text-green-600" size={16} />
                            Editar usuario
                          </button>
                          {user._id !== currentUser._id && (
                            <button
                              onClick={() => {
                                handleDelete(user._id);
                                setMobileMenuOpen(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 flex items-center"
                            >
                              <FiTrash2 className="mr-2 text-red-600" size={16} />
                              Eliminar usuario
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* User details */}
              <div className="mt-3 space-y-2 text-sm border-t pt-3">
                <div className="flex items-center text-gray-600">
                  <FiMail className="mr-2 text-gray-400" size={14} />
                  <span className="truncate flex-1">{user.email}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <FiPhone className="mr-2 text-gray-400" size={14} />
                  <span>{user.phone}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <FiClock className="mr-2 text-gray-400" size={14} />
                  <span>Tiempo conectado: {user.formattedActiveTime || '0h 0m'}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <FiActivity className="mr-2 text-gray-400" size={14} />
                  <span>
                    {user.loginStats?.lastLogin 
                      ? `Último acceso: ${formatDistanceToNow(new Date(user.loginStats.lastLogin), { addSuffix: true, locale: es })}`
                      : 'Nunca conectado'}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    user.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {user.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de creación/edición */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono *
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>
              
              {!editingUser && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <p className="text-sm text-yellow-700">
                    <strong>Sin contraseña necesaria</strong><br />
                    Se generará una contraseña aleatoria automáticamente.
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rol
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="user">Usuario normal</option>
                  <option value="admin">Administrador</option>
                  <option value="owner">Dueño (Owner)</option>
                </select>
              </div>
              
              {formData.role === 'admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Admin (1-4)
                  </label>
                  <select
                    value={formData.adminId}
                    onChange={(e) => setFormData({...formData, adminId: parseInt(e.target.value)})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value={1}>Admin 1</option>
                    <option value={2}>Admin 2</option>
                    <option value={3}>Admin 3</option>
                    <option value={4}>Admin 4</option>
                  </select>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Usuario activo (puede iniciar sesión)
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800"
                >
                  {editingUser ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de permisos */}
      {showPermissionsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Permisos de {selectedUser.name}
            </h2>
            
            <PermissionEditor
              permissions={selectedUser.permissions}
              onChange={(newPermissions) => setSelectedUser({
                ...selectedUser,
                permissions: newPermissions
              })}
            />
            
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowPermissionsModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpdatePermissions}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800"
              >
                Guardar Permisos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de actividad */}
      {showActivityModal && activityData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Actividad de {activityData.user.name}
            </h2>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600">Tiempo total</p>
                <p className="text-2xl font-bold text-blue-700">{activityData.user.formattedTime}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-600">Estado</p>
                <p className="text-2xl font-bold text-green-700">
                  {activityData.user.isOnline ? 'En línea' : 'Desconectado'}
                </p>
              </div>
            </div>
            
            {activityData.user.currentSession?.isActive && (
              <div className="bg-yellow-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold mb-2">Sesión actual</h3>
                <p className="text-sm">Inició: {new Date(activityData.user.currentSession.loginTime).toLocaleString()}</p>
                <p className="text-sm">IP: {activityData.user.currentSession.ipAddress || 'No disponible'}</p>
              </div>
            )}
            
            <h3 className="font-semibold mb-3">Últimas sesiones</h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {activityData.user.loginHistory?.map((session, idx) => (
                <div key={idx} className="bg-gray-50 p-3 rounded-lg text-sm">
                  <div className="flex justify-between">
                    <span>{new Date(session.loginTime).toLocaleDateString()}</span>
                    <span className="font-medium">{session.duration} minutos</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {new Date(session.loginTime).toLocaleTimeString()} - 
                    {session.logoutTime ? new Date(session.logoutTime).toLocaleTimeString() : 'Activo'}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowActivityModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;